import { Box, Button, MenuItem } from '@mui/material';
import { Plus } from '@ankr.com/ui';
import React from 'react';

import { AccountPermission } from 'modules/permissions/constants';
import { AddToWalletMenuItem } from 'modules/chains/components/ChainMenu';
import { ChainSelectorsProvider } from 'modules/chains/components/ChainSelectorsProvider';
import { CopyEndpointModalWrapper } from 'modules/chains/components/CopyEndpointModal';
import { GuardAccountPermission } from 'modules/account/components/GuardAccountPermission';
import { GuardResolution } from 'modules/common/components/GuardResolution';
import { MenuButton } from 'modules/common/components/MenuButton';
import { useTranslation } from 'modules/i18n/hooks/useTranslation';
import { JWT } from 'domains/jwtToken/types';

import { ChainProjects } from '../ChainProjects';
import {
  HIDE_MM_BUTTON,
  IUsePrivateChainCardActionsProps,
  usePrivateChainCardActions,
} from './hooks/usePrivateChainCardActions';
import { privateChainCardActionsTranslation } from './translation';
import { usePrivateChainCardActionsStyles } from './usePrivateChainCardActionsStyles';

interface IPrivateChainCardActionsProps
  extends IUsePrivateChainCardActionsProps {
  isEndpointLocked: boolean;
  anchorEl: null | HTMLElement;
  filteredJwtTokens: JWT[];
  handleOpenChainMenu: (event: React.MouseEvent<HTMLButtonElement>) => void;
  isCardView?: boolean;
  isChainProjectsEmpty?: boolean;
  onOpenAddToProjectsDialog: () => void;
  open: boolean;
  projectsLoading: boolean;
}

export const PrivateChainCardActions = ({
  anchorEl,
  chain,
  chainProjects,
  filteredJwtTokens,
  handleClose,
  handleOpenChainMenu,
  isCardView,
  isChainProjectsEmpty,
  isEndpointLocked,
  onOpenAddToProjectsDialog,
  open,
  projectsLoading,
}: IPrivateChainCardActionsProps) => {
  const {
    handleOpenAddToProjectsDialog,
    isAddToMetamaskButtonVisible,
    projectChain,
    selectedToken,
  } = usePrivateChainCardActions({
    chain,
    chainProjects,
    handleClose,
    onOpenAddToProjectsDialog,
  });

  const { classes, cx } = usePrivateChainCardActionsStyles();
  const { keys, t } = useTranslation(privateChainCardActionsTranslation);

  return (
    <GuardAccountPermission permission={AccountPermission.ChainItem}>
      <div className={classes.root}>
        <ChainProjects
          chainProjects={chainProjects}
          isLoadingProjects={projectsLoading}
        />
        {!isEndpointLocked && !isChainProjectsEmpty && (
          <CopyEndpointModalWrapper
            buttonClassName={cx(classes.copyEndpointButton, {
              [classes.largeButton]: !isCardView,
            })}
            buttonProps={{
              variant: 'outlined',
              disabled: projectsLoading,
            }}
            chain={projectChain}
            hasProjectSelector={filteredJwtTokens.length > 1}
            isIconButton={isCardView}
            jwtTokens={filteredJwtTokens}
            userEndpointToken={
              selectedToken ?? filteredJwtTokens[0].userEndpointToken
            }
          />
        )}
        {isChainProjectsEmpty && (
          <Button
            className={cx(classes.addToProjectButtonEmpty, {
              [classes.largeButton]: !isCardView,
            })}
            children={isCardView ? null : t(keys.addToProjectButton)}
            onClick={handleOpenAddToProjectsDialog}
            size="small"
            startIcon={<Plus />}
            variant="outlined"
          />
        )}
        {isAddToMetamaskButtonVisible || !isChainProjectsEmpty ? (
          <MenuButton
            anchorEl={anchorEl}
            buttonProps={{
              size: 'small',
              variant: 'outlined',
              className: cx(classes.moreButton, {
                [classes.moreButtonActive]: open,
              }),
              loading: projectsLoading,
            }}
            classes={{ paper: classes.menuPaper }}
            iconMoreClassName={classes.moreButtonIcon}
            keepMounted
            onClose={handleClose}
            onOpen={handleOpenChainMenu}
            open={open}
          >
            {!HIDE_MM_BUTTON && (
              <ChainSelectorsProvider chain={chain}>
                <AddToWalletMenuItem chain={chain} closeMenu={handleClose} />
              </ChainSelectorsProvider>
            )}
            {!isChainProjectsEmpty && (
              <GuardResolution protectedResolution="smDown">
                <GuardAccountPermission
                  permission={AccountPermission.JwtManagerRead}
                >
                  <MenuItem
                    children={t(keys.addToProjectButton)}
                    className={classes.addToProjectButton}
                    component={Button}
                    onClick={handleOpenAddToProjectsDialog}
                    size="small"
                    startIcon={<Plus className={classes.plusIcon} />}
                    sx={{
                      whiteSpace: 'nowrap',
                    }}
                  />
                </GuardAccountPermission>
              </GuardResolution>
            )}
          </MenuButton>
        ) : (
          <Box
            className={cx(classes.menuButtonPlaceholder, {
              [classes.menuButtonPlaceholderHidden]: isCardView,
            })}
          />
        )}
      </div>
    </GuardAccountPermission>
  );
};
